import { Partner_partner as TPartner } from 'data/queries/types/Partner'
import { Audiences, DateFormat } from 'data/types/graphql-global-types'
import { isValid } from 'date-fns'
import { utcDate } from './date'

type TLookup = {
  [key in DateFormat as string]: string
}

export type TDeserializedPartner = Omit<TPartner, 'terminationDate'> & {
  terminationDate: Date | null
  welcomeEmailDateValue: Date | null
}

const DATE_FORMAT_LOOKUP: TLookup = {
  [DateFormat.MMDDYYYY_SLASHES]: 'MM/DD/YYYY',
  [DateFormat.DDMMYYYY_SLASHES]: 'DD/MM/YYYY',
  [DateFormat.YYYYMMDD_SLASHES]: 'YYYY/MM/DD',
  [DateFormat.MMDDYYYY_DASHES]: 'MM-DD-YYYY',
  [DateFormat.DDMMYYYY_DASHES]: 'DD-MM-YYYY',
  [DateFormat.YYYYMMDD_DASHES]: 'YYYY-MM-DD',
}

// BE unable to return a null value for dateFormat b/c....graphene?
// They are returning 'NONE', which we want to treat the same as null
const dateFormatExists = (dateFormat?: string | null) => {
  return dateFormat && dateFormat !== 'NONE'
}

const deserializePartner = {
  dateFormat: (dateFormat?: string | null) => {
    let value = dateFormat || ''
    if (Object.keys(DATE_FORMAT_LOOKUP).indexOf(value) > -1) {
      value = DATE_FORMAT_LOOKUP[value]
    } else if (dateFormat === 'NONE') {
      // see also: dateFormatExists
      value = ''
    }

    return value
  },
  domains: (domains: TPartner['domains']) => {
    return (Array.isArray(domains) && domains.join(', ')) || ''
  },
  welcomeEmailDateValue: (date: string | null) => {
    let welcomeEmailDateValue = null

    if (date && isValid(new Date(date))) {
      welcomeEmailDateValue = utcDate(date)
    }

    return welcomeEmailDateValue
  },
  terminationDate: (date?: string | null) => {
    let termDate = null

    if (date && isValid(new Date(date))) {
      termDate = utcDate(date)
    }

    return termDate
  },
}

const serializePartner = {
  dateFormat: (input?: string | null) => {
    const normalizedDate = Object.keys(DATE_FORMAT_LOOKUP).find(key => {
      return DATE_FORMAT_LOOKUP[key] === input
    })

    if (normalizedDate) {
      return normalizedDate as DateFormat
    }

    return null
  },
  domains: (input?: { domain: string }[] | null) => {
    if (Array.isArray(input)) {
      return input.map(d => d.domain.trim())
    }

    return null
  },
  // EM-3876: welcome_email_date only carries a calendar date - the backend (Partner.save())
  // normalizes it to midnight UTC regardless of what's sent, so there's no need for a time/
  // timezone picker here. Build midnight UTC directly from the Y/M/D the user picked in the
  // date picker (local getters, matching formatDatepickerDate's convention elsewhere in this file).
  buildWelcomeEmailDate: (date: Date | null): string | null => {
    if (!date) return null

    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    ).toISOString()
  },
}

const isRapidPartner = (partner?: TPartner | null) => {
  return Boolean(partner && partner?.partnerType === Audiences.partnership)
}

const isCorporatePartner = (partner?: TPartner | null) => {
  return Boolean(partner && partner?.partnerType === Audiences.corporate)
}

const formatProgramName = (partnerName: string) => {
  const spaces = /\s+/g
  const cleanedName = partnerName.slice(0, 15).replace(spaces, '-').toLowerCase()

  return `${cleanedName}-benefit-program`
}

const getAltBenefitName = (benefitName: string) => {
  const name = benefitName.toLowerCase()
  switch (name) {
    case 'digital membership':
      return 'Digital Subscription'
    case 'device membership':
      return 'All-Access Membership'
    case 'introductory all-access membership':
      return 'Guide Subscription'
    default:
      return benefitName
  }
}

export {
  formatProgramName,
  dateFormatExists,
  deserializePartner,
  serializePartner,
  isRapidPartner,
  isCorporatePartner,
  getAltBenefitName,
  DATE_FORMAT_LOOKUP,
}
